import { z } from "zod";

import { brandSocialAffinitiesSchema } from "./brand-profile.ts";
import {
  getSocialPostCanvasSize,
  socialPostCarouselTypeSchema,
  socialPostPlatformSchema,
  type SocialPostCarouselType,
  type SocialPostPlatform,
} from "./social-post-scene.ts";
import {
  socialPostLayoutTemplateIdSchema,
  socialPostLayoutTemplateNameSchema,
} from "./social-post-layout-templates.ts";

export const SOCIAL_POST_CAROUSEL_MIN_SLIDES = 2;
export const SOCIAL_POST_CAROUSEL_MAX_SLIDES = 10;
export const SOCIAL_POST_CAROUSEL_MAX_HOOK_HEADLINE_CHARS = 90;
export const SOCIAL_POST_CAROUSEL_MAX_CTA_CHARS = 80;
export const SOCIAL_POST_CAROUSEL_MAX_TEMPLATE_OPTIONS = 12;

export const SOCIAL_POST_CAROUSEL_VISUAL_CONTINUITY_MODES = [
  "recurring_people",
  "recurring_setting_or_object",
  "thematic_only",
] as const;
export type SocialPostCarouselVisualContinuityMode =
  (typeof SOCIAL_POST_CAROUSEL_VISUAL_CONTINUITY_MODES)[number];

export const SOCIAL_POST_CAROUSEL_SLIDE_ROLES = [
  "hook",
  "context",
  "problem",
  "insight",
  "proof",
  "process",
  "feature",
  "objection",
  "transition",
  "cta",
] as const;
export type SocialPostCarouselSlideRole = (typeof SOCIAL_POST_CAROUSEL_SLIDE_ROLES)[number];
export const socialPostCarouselSlideRoleSchema = z.enum(SOCIAL_POST_CAROUSEL_SLIDE_ROLES);

export type SocialPostCarouselPlanIssueCode =
  | "banned_word"
  | "cta_limit"
  | "em_dash"
  | "hashtag_format"
  | "hook_limit"
  | "pattern_mismatch"
  | "slide_count";

export type SocialPostCarouselPlanIssue = {
  code: SocialPostCarouselPlanIssueCode;
  path: Array<string | number>;
  message: string;
};

const nonEmptyStringSchema = z.string().trim().min(1);

export const socialPostCarouselFormatKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/);

const orderedUniqueStringArraySchema = z
  .array(z.string().trim().min(1).max(80))
  .default([])
  .transform((values) =>
    Array.from(new Set(values.map((value) => value.trim().toLowerCase()))).toSorted(),
  );

export const socialPostCarouselTemplateOptionV1Schema = z
  .object({
    templateId: socialPostLayoutTemplateIdSchema,
    templateName: socialPostLayoutTemplateNameSchema,
  })
  .strict();
export type SocialPostCarouselTemplateOptionV1 = z.infer<
  typeof socialPostCarouselTemplateOptionV1Schema
>;

const assertCarouselTemplateOptionIdsUnique = (
  value: { templateOptions?: SocialPostCarouselTemplateOptionV1[] },
  ctx: z.RefinementCtx,
) => {
  const seen = new Set<string>();
  for (const [index, option] of (value.templateOptions ?? []).entries()) {
    if (seen.has(option.templateId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["templateOptions", index, "templateId"],
        message: "Carousel template options must use unique template ids.",
      });
    }
    seen.add(option.templateId);
  }
};

export const socialPostCarouselFormatPatternRowV1Schema = z
  .object({
    role: socialPostCarouselSlideRoleSchema,
    title: nonEmptyStringSchema.max(80),
    guidance: nonEmptyStringSchema.max(800),
    required: z.boolean().default(true),
    templateOptions: z
      .array(socialPostCarouselTemplateOptionV1Schema)
      .max(SOCIAL_POST_CAROUSEL_MAX_TEMPLATE_OPTIONS)
      .default([]),
  })
  .superRefine(assertCarouselTemplateOptionIdsUnique);
export type SocialPostCarouselFormatPatternRowV1 = Omit<
  z.infer<typeof socialPostCarouselFormatPatternRowV1Schema>,
  "templateOptions"
> & {
  templateOptions?: SocialPostCarouselTemplateOptionV1[];
};

const socialPostCarouselFormatTemplateOptionsSchema = z
  .array(socialPostCarouselTemplateOptionV1Schema)
  .max(SOCIAL_POST_CAROUSEL_MAX_TEMPLATE_OPTIONS);

const socialPostCarouselFormatDetailsV1Shape = {
  key: socialPostCarouselFormatKeySchema,
  name: nonEmptyStringSchema.max(100),
  minSlides: z
    .number()
    .int()
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES)
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  maxSlides: z
    .number()
    .int()
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES)
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  pattern: z
    .array(socialPostCarouselFormatPatternRowV1Schema)
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES)
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  templateOptions: socialPostCarouselFormatTemplateOptionsSchema.optional(),
  classifierGuidance: nonEmptyStringSchema.max(1200),
  example: nonEmptyStringSchema.max(2000),
  socialAffinities: brandSocialAffinitiesSchema.optional(),
  bannedWords: orderedUniqueStringArraySchema,
};

const validateSocialPostCarouselFormatDetails = (
  value: {
    minSlides: number;
    maxSlides: number;
    pattern: SocialPostCarouselFormatPatternRowV1[];
    templateOptions?: SocialPostCarouselTemplateOptionV1[];
  },
  ctx: z.RefinementCtx,
) => {
  assertCarouselTemplateOptionIdsUnique(value, ctx);

  if (value.minSlides > value.maxSlides) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["minSlides"],
      message: "Minimum slide count cannot exceed maximum slide count.",
    });
  }

  if (value.pattern.length < value.minSlides) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["pattern"],
      message: "Pattern must contain at least the minimum slide count.",
    });
  }

  if (value.pattern.length > value.maxSlides) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["pattern"],
      message: "Pattern cannot contain more rows than the maximum slide count.",
    });
  }
};

export const socialPostCarouselFormatDetailsV1Schema = z
  .object(socialPostCarouselFormatDetailsV1Shape)
  .superRefine(validateSocialPostCarouselFormatDetails);
export type SocialPostCarouselFormatDetailsV1 = z.infer<
  typeof socialPostCarouselFormatDetailsV1Schema
>;

export const socialPostCarouselFormatSnapshotV1Schema = z
  .object({
    type: z.literal("social_post.carousel_format_snapshot"),
    version: z.literal(1),
    ...socialPostCarouselFormatDetailsV1Shape,
  })
  .superRefine(validateSocialPostCarouselFormatDetails);
export type SocialPostCarouselFormatSnapshotV1 = z.infer<
  typeof socialPostCarouselFormatSnapshotV1Schema
>;

export type SocialPostCarouselFormatCtaPatternIssue = {
  path: Array<string | number>;
  message: string;
};

export const collectSocialPostCarouselFormatCtaPatternIssues = (
  pattern: readonly SocialPostCarouselFormatPatternRowV1[],
): SocialPostCarouselFormatCtaPatternIssue[] => {
  const ctaIndexes = pattern.flatMap((row, index) => (row.role === "cta" ? [index] : []));
  if (ctaIndexes.length > 1) {
    return [
      {
        path: ["pattern"],
        message: "Carousel formats can include at most one CTA pattern row.",
      },
    ];
  }

  const ctaIndex = ctaIndexes[0];
  if (ctaIndex !== undefined && ctaIndex !== pattern.length - 1) {
    return [
      {
        path: ["pattern", ctaIndex, "role"],
        message: "The CTA pattern row must be the final pattern row.",
      },
    ];
  }

  return [];
};

export const collectLegacySocialPostCarouselTemplateOptions = (
  pattern: readonly SocialPostCarouselFormatPatternRowV1[],
): SocialPostCarouselTemplateOptionV1[] => {
  const seen = new Set<string>();
  const options: SocialPostCarouselTemplateOptionV1[] = [];

  for (const row of pattern) {
    for (const option of row.templateOptions ?? []) {
      if (seen.has(option.templateId)) {
        continue;
      }

      seen.add(option.templateId);
      options.push(option);
    }
  }

  return options;
};

export const resolveSocialPostCarouselTemplateOptions = (
  format: Pick<SocialPostCarouselFormatDetailsV1, "pattern" | "templateOptions">,
): SocialPostCarouselTemplateOptionV1[] =>
  format.templateOptions ?? collectLegacySocialPostCarouselTemplateOptions(format.pattern);

export const resolveSocialPostCarouselFormatPatternRow = (input: {
  pattern: readonly SocialPostCarouselFormatPatternRowV1[];
  slideIndex: number;
  slideCount: number;
  slideRole?: SocialPostCarouselSlideRole;
}): SocialPostCarouselFormatPatternRowV1 | null => {
  if (input.slideIndex < 1 || input.slideIndex > input.slideCount) {
    return null;
  }

  const ctaRows = input.pattern.filter((row) => row.role === "cta");
  const terminalCtaRow = ctaRows.at(-1);
  const contentRows = input.pattern.filter((row) => row.role !== "cta");

  if (
    terminalCtaRow &&
    input.slideIndex === input.slideCount &&
    (terminalCtaRow.required || input.slideRole === undefined || input.slideRole === "cta")
  ) {
    return terminalCtaRow;
  }

  const positionalRow = contentRows[input.slideIndex - 1];
  if (positionalRow) {
    return positionalRow;
  }

  if (input.slideRole && input.slideRole !== "cta") {
    return contentRows.find((row) => row.role === input.slideRole) ?? null;
  }

  return null;
};

export const socialPostCarouselSlideV1Schema = z.object({
  id: nonEmptyStringSchema.max(80),
  index: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  role: socialPostCarouselSlideRoleSchema,
  headline: nonEmptyStringSchema.max(160),
  body: z.string().trim().min(1).max(420).nullable().default(null),
  cta: z.string().trim().min(1).max(160).nullable().default(null),
  visualBrief: nonEmptyStringSchema.max(800),
  speakerNotes: z.string().trim().min(1).max(800).nullable().default(null),
});
export type SocialPostCarouselSlideV1 = z.infer<typeof socialPostCarouselSlideV1Schema>;

export const socialPostCarouselRecurringCastMemberV1Schema = z
  .object({
    label: nonEmptyStringSchema.max(60),
    description: nonEmptyStringSchema.max(300),
    roleInStory: nonEmptyStringSchema.max(200),
    appearanceSlideIndexes: z
      .array(z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES))
      .min(1)
      .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
  })
  .superRefine((value, ctx) => {
    const seen = new Set<number>();
    for (const [index, slideIndex] of value.appearanceSlideIndexes.entries()) {
      if (seen.has(slideIndex)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["appearanceSlideIndexes", index],
          message: "Recurring cast slide indexes must be unique.",
        });
      }
      seen.add(slideIndex);
    }
  });
export type SocialPostCarouselRecurringCastMemberV1 = z.infer<
  typeof socialPostCarouselRecurringCastMemberV1Schema
>;

export const socialPostCarouselVisualContinuityV1Schema = z
  .object({
    mode: z.enum(SOCIAL_POST_CAROUSEL_VISUAL_CONTINUITY_MODES),
    rationale: nonEmptyStringSchema.max(500),
    recurringCast: z.array(socialPostCarouselRecurringCastMemberV1Schema).max(4).default([]),
    recurringSettingOrObject: z.string().trim().min(1).max(300).nullable().default(null),
    continuityRules: z.array(nonEmptyStringSchema.max(220)).max(6).default([]),
  })
  .superRefine((value, ctx) => {
    const seenCastLabels = new Set<string>();
    for (const [index, castMember] of value.recurringCast.entries()) {
      const normalizedLabel = castMember.label.toLowerCase();
      if (seenCastLabels.has(normalizedLabel)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recurringCast", index, "label"],
          message: "Recurring cast labels must be unique.",
        });
      }
      seenCastLabels.add(normalizedLabel);
    }

    if (value.mode === "recurring_people" && value.recurringCast.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recurringCast"],
        message: "Recurring people continuity requires at least one cast member.",
      });
    }

    if (value.mode !== "recurring_people" && value.recurringCast.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recurringCast"],
        message: "Recurring cast is only allowed when mode is recurring_people.",
      });
    }

    if (value.mode === "recurring_setting_or_object" && !value.recurringSettingOrObject) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recurringSettingOrObject"],
        message: "Recurring setting or object continuity requires a setting or object.",
      });
    }

    if (value.mode === "thematic_only" && value.recurringSettingOrObject) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recurringSettingOrObject"],
        message: "Thematic-only continuity cannot define a recurring setting or object.",
      });
    }
  });
export type SocialPostCarouselVisualContinuityV1 = z.infer<
  typeof socialPostCarouselVisualContinuityV1Schema
>;

export const socialPostCarouselPlanV1Schema = z
  .object({
    type: z.literal("social_post.carousel_plan"),
    version: z.literal(1),
    platform: socialPostPlatformSchema,
    postType: socialPostCarouselTypeSchema,
    format: socialPostCarouselFormatSnapshotV1Schema,
    slides: z
      .array(socialPostCarouselSlideV1Schema)
      .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES)
      .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES),
    visualContinuity: socialPostCarouselVisualContinuityV1Schema.optional(),
    caption: nonEmptyStringSchema.max(2200),
    hashtags: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
  })
  .superRefine((value, ctx) => {
    if (!getSocialPostCanvasSize(value.platform, value.postType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["postType"],
        message: "Unsupported carousel post type for platform.",
      });
    }

    const slideIndexes = new Set<number>();
    for (const [arrayIndex, slide] of value.slides.entries()) {
      if (slideIndexes.has(slide.index)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["slides", arrayIndex, "index"],
          message: "Carousel slide indexes must be unique.",
        });
      }
      slideIndexes.add(slide.index);
    }

    const expectedIndexes = Array.from({ length: value.slides.length }, (_, index) => index + 1);
    for (const expectedIndex of expectedIndexes) {
      if (!slideIndexes.has(expectedIndex)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["slides"],
          message: "Carousel slide indexes must be consecutive from 1.",
        });
        break;
      }
    }

    for (const [castIndex, castMember] of (value.visualContinuity?.recurringCast ?? []).entries()) {
      for (const [appearanceIndex, slideIndex] of castMember.appearanceSlideIndexes.entries()) {
        if (!slideIndexes.has(slideIndex)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [
              "visualContinuity",
              "recurringCast",
              castIndex,
              "appearanceSlideIndexes",
              appearanceIndex,
            ],
            message: "Recurring cast slide indexes must reference slides in this carousel.",
          });
        }
      }
    }
  });
export type SocialPostCarouselPlanV1 = z.infer<typeof socialPostCarouselPlanV1Schema>;

export const isCarouselFeedPostType = (postType: string): postType is SocialPostCarouselType =>
  socialPostCarouselTypeSchema.safeParse(postType).success;

export const getSocialPostCarouselCanvasSize = (
  platform: SocialPostPlatform,
  postType: SocialPostCarouselType,
) => getSocialPostCanvasSize(platform, postType);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const bannedWordPattern = (word: string): RegExp => {
  const escaped = escapeRegExp(word.trim().toLowerCase());
  return word.includes(" ")
    ? new RegExp(escaped, "i")
    : new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i");
};

const validHashtagPattern = /^[\p{L}\p{N}_]+$/u;

const pushTextRuleIssues = (
  issues: SocialPostCarouselPlanIssue[],
  input: {
    path: Array<string | number>;
    value: string | null;
    bannedWords: string[];
  },
) => {
  if (!input.value) {
    return;
  }

  if (input.value.includes("—")) {
    issues.push({
      code: "em_dash",
      path: input.path,
      message: "Carousel copy must not contain em dashes.",
    });
  }

  for (const bannedWord of input.bannedWords) {
    if (bannedWordPattern(bannedWord).test(input.value)) {
      issues.push({
        code: "banned_word",
        path: input.path,
        message: `Carousel copy contains banned word "${bannedWord}".`,
      });
    }
  }
};

export const collectSocialPostCarouselPlanIssues = (
  plan: SocialPostCarouselPlanV1,
): SocialPostCarouselPlanIssue[] => {
  const issues: SocialPostCarouselPlanIssue[] = [];
  const slideCount = plan.slides.length;

  if (slideCount < plan.format.minSlides || slideCount > plan.format.maxSlides) {
    issues.push({
      code: "slide_count",
      path: ["slides"],
      message: `Carousel format "${plan.format.key}" requires ${plan.format.minSlides}-${plan.format.maxSlides} slides.`,
    });
  }

  const hookSlideIndexes = plan.slides
    .filter((slide) => slide.role === "hook")
    .map((slide) => slide.index);
  if (hookSlideIndexes.length !== 1 || hookSlideIndexes[0] !== 1) {
    issues.push({
      code: "hook_limit",
      path: ["slides"],
      message: "Carousel plans must include exactly one hook slide, and it must be slide 1.",
    });
  }

  const ctaSlides = plan.slides.filter((slide) => slide.role === "cta" || slide.cta);
  const ctaSlideIndexes = ctaSlides.map((slide) => slide.index);
  if (ctaSlideIndexes.length > 1) {
    issues.push({
      code: "cta_limit",
      path: ["slides"],
      message: "Carousel plans can include at most one CTA slide.",
    });
  }

  for (const slide of ctaSlides) {
    if (slide.index !== slideCount) {
      issues.push({
        code: "cta_limit",
        path: ["slides", slide.index - 1, slide.role === "cta" ? "role" : "cta"],
        message: "Carousel CTA must be on the final slide.",
      });
    }
  }

  for (const [arrayIndex, slide] of plan.slides.entries()) {
    const expectedPatternRow = resolveSocialPostCarouselFormatPatternRow({
      pattern: plan.format.pattern,
      slideIndex: slide.index,
      slideCount,
    });
    if (
      expectedPatternRow &&
      expectedPatternRow.required &&
      slide.role !== expectedPatternRow.role
    ) {
      issues.push({
        code: "pattern_mismatch",
        path: ["slides", arrayIndex, "role"],
        message: `Slide ${slide.index} must use the ${expectedPatternRow.role} role for the selected format.`,
      });
    }

    if (
      slide.role === "hook" &&
      slide.headline.length > SOCIAL_POST_CAROUSEL_MAX_HOOK_HEADLINE_CHARS
    ) {
      issues.push({
        code: "hook_limit",
        path: ["slides", arrayIndex, "headline"],
        message: `Hook headline must be ${SOCIAL_POST_CAROUSEL_MAX_HOOK_HEADLINE_CHARS} characters or fewer.`,
      });
    }

    if (slide.cta && slide.cta.length > SOCIAL_POST_CAROUSEL_MAX_CTA_CHARS) {
      issues.push({
        code: "cta_limit",
        path: ["slides", arrayIndex, "cta"],
        message: `CTA copy must be ${SOCIAL_POST_CAROUSEL_MAX_CTA_CHARS} characters or fewer.`,
      });
    }

    for (const [field, value] of [
      ["headline", slide.headline],
      ["body", slide.body],
      ["cta", slide.cta],
      ["visualBrief", slide.visualBrief],
      ["speakerNotes", slide.speakerNotes],
    ] as const) {
      pushTextRuleIssues(issues, {
        path: ["slides", arrayIndex, field],
        value,
        bannedWords: plan.format.bannedWords,
      });
    }
  }

  pushTextRuleIssues(issues, {
    path: ["caption"],
    value: plan.caption,
    bannedWords: plan.format.bannedWords,
  });

  for (const [index, hashtag] of plan.hashtags.entries()) {
    if (!validHashtagPattern.test(hashtag)) {
      issues.push({
        code: "hashtag_format",
        path: ["hashtags", index],
        message:
          "Carousel hashtags must omit # and contain only letters, numbers, or underscores with no spaces.",
      });
    }
  }

  return issues;
};

export const assertSocialPostCarouselPlan = (plan: SocialPostCarouselPlanV1): void => {
  const issues = collectSocialPostCarouselPlanIssues(plan);
  if (issues.length > 0) {
    throw new Error(`Invalid social post carousel plan: ${issues[0]?.message ?? "unknown issue"}`);
  }
};
